import React, { useState } from 'react';

type SheetData = string[][];

interface Props {
  data: SheetData;
  title: string;
  onRowClick?: (guid: string) => void;
  guidColumnIndex?: number;
  onEdit?: (rowIndex: number, newRow: string[]) => void;
  allowBlankEdit?: boolean; // ADD THIS
}

const ScrollableSheetViewer: React.FC<Props> = ({ data, title, onRowClick, guidColumnIndex, onEdit, allowBlankEdit }) => {
  const header = data[0];
  const [editingCell, setEditingCell] = useState<{ row: number; col: number } | null>(null);
  const [editedData, setEditedData] = useState<SheetData>(data.slice(1));

  // Update local state if new data comes in
  React.useEffect(() => {
    setEditedData(data.slice(1));
  }, [data]);

  const handleCellChange = (value: string, row: number, col: number) => {
    const newData = [...editedData];
    if (!newData[row]) newData[row] = [];
    newData[row][col] = value;
    setEditedData(newData);
    if (onEdit) {
      console.log("📣 Cell edited! Calling onEdit with:", row, newData[row]);
      onEdit(row + 1, newData[row]);
    }
  };

  const renderCell = (_: string | null | undefined, rowIdx: number, colIdx: number) => {
    const cell = editedData[rowIdx]?.[colIdx] ?? '';
    const isEditing = editingCell?.row === rowIdx && editingCell?.col === colIdx;
    const displayValue = cell;

    if (isEditing) {
      const safeValue =
        editedData[rowIdx] && editedData[rowIdx][colIdx] !== undefined
          ? editedData[rowIdx][colIdx]
          : '';

      return (
        <input
          type="text"
          value={safeValue}
          autoFocus
          onChange={(e) => handleCellChange(e.target.value, rowIdx, colIdx)}
          onBlur={() => setEditingCell(null)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') setEditingCell(null);
          }}
          style={{
            width: '100%',
            height: '100%',
            border: 'none',
            outline: 'none',
            fontFamily: 'monospace',
            fontSize: '12px',
          }}
        />
      );
    }

    return (
      <div
        onClick={() => {
          if (cell || allowBlankEdit) {
            setEditingCell({ row: rowIdx, col: colIdx });
          }
        }}
        style={{
          cursor: cell || allowBlankEdit ? 'pointer' : 'default',
          minHeight: '16px',
          minWidth: '100%',
          padding: '2px',
          backgroundColor: cell || allowBlankEdit ? '#fff' : 'transparent',
          border: !cell && allowBlankEdit ? '1px dashed #ccc' : undefined,
        }}
      >
        {displayValue || ' '}
      </div>
    );
};

  if (!data.length) return null;

  return (
    <div style={{ marginBottom: '20px', width: '100%', maxWidth: '33%', minWidth: '300px' }}>
      <h3 style={{ fontSize: '16px', fontWeight: 600 }}>{title}</h3>
      <div
        style={{
          height: '300px',
          overflow: 'auto',
          border: '1px solid #ccc',
          borderRadius: '4px',
          backgroundColor: '#fff',
          fontFamily: 'monospace',
          fontSize: '12px',
          boxShadow: '0 2px 6px rgba(0,0,0,0.05)',
        }}
      >
        <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
          <thead>
            <tr>
              {header.map((_, colIdx) => (
                <th
                  key={colIdx}
                  style={{
                    position: 'sticky',
                    top: 0,
                    background: '#f0f0f0',
                    border: '1px solid #ccc',
                    padding: '4px',
                    fontWeight: 600,
                  }}
                >
                  {header[colIdx]}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {editedData.map((row, rowIdx) => {
              const paddedRow = [...row];
              while (paddedRow.length < header.length) {
                paddedRow.push('');
              }

              return (
                <tr
                  key={rowIdx}
                  onClick={() => onRowClick && onRowClick(row[guidColumnIndex ?? 0])}
                  style={{ cursor: onRowClick ? 'pointer' : 'default' }}
                >
                  {paddedRow.map((cell, colIdx) => (
                    <td
                      key={colIdx}
                      style={{
                        border: '1px solid #eee',
                        padding: '4px',
                        backgroundColor: rowIdx % 2 === 0 ? '#fff' : '#f9f9f9',
                        wordBreak: 'break-word',
                      }}
                    >
                      {renderCell(cell, rowIdx, colIdx)}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ScrollableSheetViewer;